import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { chatApiService, streamChat, resumeNavStream, cancelStream, restoreCheckpoint } from '../../services/chatApi';
import { ChatSession, ChatMessage, MessageContext, AssistantMessage, DisplayContent } from '../../types/domain';
import type { NavSuggestedAction, NavReportData, NavWidgetEvent, NavDisplayEvent, NavContentSummaryEvent, NavThreadSummaryEvent } from '../../types/api';
import { useSnackbar } from '../../../../providers/SnackbarProvider';
import { generateMessageId, generateOptimisticSessionId } from '../../utils/messageUtils';

const isAbortError = (error: unknown): boolean => {
  const name = (error as { name?: string })?.name;
  const code = (error as { code?: string })?.code;
  return name === 'CanceledError' || name === 'AbortError' || code === 'ERR_CANCELED';
};

export const chatQueryKeys = {
  all: ['chat'] as const,
  sessions: () => [...chatQueryKeys.all, 'sessions'] as const,
  session: (id: string) => [...chatQueryKeys.all, 'session', id] as const,
  currentSession: () => [...chatQueryKeys.all, 'currentSession'] as const,
};

export const useChatSessions = (enabled: boolean = false) => {
  return useQuery({
    queryKey: chatQueryKeys.sessions(),
    queryFn: () => chatApiService.getSessions(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    enabled,
  });
};

export const useCurrentSession = () => {
  return useQuery({
    queryKey: chatQueryKeys.currentSession(),
    queryFn: () => chatApiService.getCurrentSession(),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Send a message mutation
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();

  const baseMutation = useMutation({
    mutationFn: async (data: {
      content: string;
      context?: MessageContext;
      messageId: string;
      signal?: AbortSignal;
    }) => {
      return chatApiService.sendMessage(data.content, data.context, data.messageId);
    },
    onMutate: async ({ content, context, messageId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: chatQueryKeys.sessions() });
      await queryClient.cancelQueries({ queryKey: chatQueryKeys.currentSession() });

      // Snapshot the previous values
      const previousSessions = queryClient.getQueryData<ChatSession[]>(chatQueryKeys.sessions());
      const previousCurrentSession = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());

      const currentSession = previousCurrentSession;
      if (!currentSession) {
        const optimisticUserMessage: ChatMessage = {
          id: messageId,
          content: content.trim(),
          sender: 'user',
          timestamp: new Date(),
          context,
        };

        const optimisticNewSession: ChatSession = {
          id: generateOptimisticSessionId(),
          title: 'New Chat',
          messages: [optimisticUserMessage],
          createdAt: new Date(),
          updatedAt: new Date(),
          lastMessagePreview: content.substring(0, 100),
          isActive: true,
        };

        queryClient.setQueryData(chatQueryKeys.currentSession(), optimisticNewSession);

        // Add to sessions list
        queryClient.setQueryData<ChatSession[]>(chatQueryKeys.sessions(), (old = []) => {
          return [optimisticNewSession, ...old];
        });

        return {
          previousSessions,
          previousCurrentSession,
          messageId,
          isNewSession: true,
        };
      }

      const optimisticUserMessage: ChatMessage = {
        id: messageId,
        content: content.trim(),
        sender: 'user',
        timestamp: new Date(),
        context,
      };

      // Optimistically update current session
      const optimisticSession: ChatSession = {
        ...currentSession,
        messages: [...currentSession.messages, optimisticUserMessage],
        updatedAt: new Date(),
      };

      queryClient.setQueryData(chatQueryKeys.currentSession(), optimisticSession);

      queryClient.setQueryData<ChatSession[]>(chatQueryKeys.sessions(), (old = []) => {
        const filtered = old.filter((s) => s.id !== optimisticSession.id);
        return [optimisticSession, ...filtered];
      });

      return {
        previousSessions,
        previousCurrentSession,
        messageId,
        isNewSession: false,
      };
    },
    onError: (error: Error, _variables, context) => {
      // Rollback optimistic updates on error (also covers a cancelled/aborted send)
      if (context) {
        queryClient.setQueryData(chatQueryKeys.sessions(), context.previousSessions || []);
        queryClient.setQueryData(chatQueryKeys.currentSession(), context.previousCurrentSession);
      }

      if (!isAbortError(error)) {
        showError(error.message || 'Failed to send message');
      }
    },
    onSuccess: () => {
      // The sendMessage API method returns the assistant message and has updated the session internally
      // We need to refresh our cached data to get the real session with both messages
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.sessions() });
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.currentSession() });
    },
  });

  return {
    ...baseMutation,
    mutateAsync: async (data: { content: string; context?: MessageContext; signal?: AbortSignal }) => {
      const messageId = generateMessageId();
      return baseMutation.mutateAsync({ ...data, messageId });
    },
  };
};

export const useCreateSession = () => {
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();

  return useMutation({
    mutationFn: () => chatApiService.createSession(),
    onSuccess: (newSession) => {
      queryClient.setQueryData(chatQueryKeys.currentSession(), newSession);

      queryClient.setQueryData<ChatSession[]>(chatQueryKeys.sessions(), (old = []) => {
        const filtered = old.filter((s) => s.id !== newSession.id);
        return [newSession, ...filtered];
      });
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to create new session');
    },
  });
};

export const useSwitchSession = () => {
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();

  return useMutation({
    mutationFn: (sessionId: string) => chatApiService.switchSession(sessionId),
    onSuccess: (session) => {
      queryClient.setQueryData(chatQueryKeys.currentSession(), session);

      if (session.messages.length === 0) {
        showError('This conversation has no messages.');
      }
    },
    onError: (error: Error) => {
      // Invalidate cache on error to ensure fresh data
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.sessions() });
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.currentSession() });
      showError(error.message || 'Failed to switch session');
    },
  });
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();

  return useMutation({
    mutationFn: (sessionId: string) => chatApiService.deleteSession(sessionId),
    onSuccess: (_, sessionId) => {
      // Remove from sessions list
      queryClient.setQueryData<ChatSession[]>(chatQueryKeys.sessions(), (old = []) =>
        old.filter((s) => s.id !== sessionId),
      );

      // If we deleted the current session, refresh current session
      const currentSession = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
      if (currentSession?.id === sessionId) {
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.currentSession() });
      }
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to delete session');
    },
  });
};

export const useUpdateSessionTitle = () => {
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();

  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      chatApiService.updateSessionTitle(sessionId, title),
    onMutate: async ({ sessionId, title }: { sessionId: string; title: string }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: chatQueryKeys.sessions() });
      await queryClient.cancelQueries({ queryKey: chatQueryKeys.currentSession() });

      // Snapshot the previous values
      const previousSessions = queryClient.getQueryData<ChatSession[]>(chatQueryKeys.sessions());
      const previousCurrentSession = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());

      // Optimistically update sessions list
      queryClient.setQueryData<ChatSession[]>(chatQueryKeys.sessions(), (old = []) =>
        old.map((s) => (s.id === sessionId ? { ...s, title, updatedAt: new Date() } : s)),
      );

      // Optimistically update current session if it's the one being updated
      if (previousCurrentSession?.id === sessionId) {
        const optimisticCurrentSession = {
          ...previousCurrentSession,
          title,
          updatedAt: new Date(),
        };
        queryClient.setQueryData(chatQueryKeys.currentSession(), optimisticCurrentSession);
      }

      return { previousSessions, previousCurrentSession };
    },
    onError: (error: Error, _variables, context) => {
      // Rollback optimistic updates on error
      if (context) {
        queryClient.setQueryData(chatQueryKeys.sessions(), context.previousSessions);
        queryClient.setQueryData(chatQueryKeys.currentSession(), context.previousCurrentSession);
      }

      showError(error.message || 'Failed to update session title');
    },
    onSuccess: (updatedSession) => {
      queryClient.setQueryData<ChatSession[]>(chatQueryKeys.sessions(), (old = []) =>
        old.map((s) => (s.id === updatedSession.id ? updatedSession : s)),
      );

      const currentSession = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
      if (currentSession?.id === updatedSession.id) {
        queryClient.setQueryData(chatQueryKeys.currentSession(), updatedSession);
      }
    },
  });
};

/**
 * Updates the current session's Insights Hub context (workflow_id/checkpoint_id)
 * from outside the chat itself — e.g. when the user switches overview version
 * in the Insights Hub panel — so the next message already targets it instead of
 * waiting on a backend insights_context echo.
 */
export const useSetInsightsContext = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (workflowId: string, checkpointId: string) => {
      // Always persist so new sessions inherit the right-panel context even before any session exists.
      chatApiService.setPendingInsightsContext(workflowId, checkpointId);

      const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
      if (!cur) return;
      if (cur.insightsContext?.workflowId === workflowId && cur.insightsContext?.checkpointId === checkpointId) {
        return;
      }

      const updated: ChatSession = {
        ...cur,
        insightsContext: { ...cur.insightsContext, workflowId, checkpointId },
      };
      chatApiService.syncSession(updated);
      queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
    },
    [queryClient],
  );
};

export const useSetSummaryThreadId = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (threadId: string) => {
      let cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
      cur ??= await chatApiService.getOrCreateSession();
      if (cur.summaryThreadId === threadId && cur.threadId === threadId) return;

      const updated: ChatSession = { ...cur, summaryThreadId: threadId, threadId: threadId };
      chatApiService.syncSession(updated);
      queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
    },
    [queryClient],
  );
};

/**
 * Mutation to add a system message to the current chat session
 */
export const useAddSystemMessage = () => {
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();

  return useMutation({
    mutationFn: async (data: { content: string; threadId?: string; handlerType?: string }) => {
      return chatApiService.addSystemMessage(data.content, data.threadId, data.handlerType as any);
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to add system message');
    },
    onSuccess: (result) => {
      // Update current session with the system message and thread ID
      queryClient.setQueryData(chatQueryKeys.currentSession(), result.session);

      // Also update the session in the sessions list
      queryClient.setQueryData<ChatSession[]>(chatQueryKeys.sessions(), (old = []) =>
        old.map((s) => (s.id === result.session.id ? result.session : s)),
      );
    },
  });
};

/**
 * Mutation to send widget response (user selections) back to backend via resume-stream (SSE)
 */
export const useSendWidgetResponse = () => {
  const queryClient = useQueryClient();
  const { showError, showWarning } = useSnackbar();
  const abortRef = useRef<AbortController | null>(null);

  return useMutation({
    mutationFn: async (data: { widgetId: string; selectedOptions: string[]; buttonAction?: string; feedbackText?: string }) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const session = await chatApiService.getCurrentSession();
      if (!session?.threadId) throw new Error('No active thread for widget response');

      let accumulated = '';
      let navigatePanel: string | undefined;
      let navigateWorkflowId: string | undefined;
      let hadRouting = false;
      const capturedReasoning: { type: string; text: string }[] = [];
      const capturedSuggestedActions: NavSuggestedAction[] = [];
      let capturedReportData: NavReportData | undefined;
      let capturedReportWorkflowId: string | undefined;
      let capturedWidget: NavWidgetEvent | undefined;
      let capturedDisplayContent: DisplayContent | undefined;
      let contentSummaryReceived = false;

      await resumeNavStream(
        {
          thread_id: session.threadId,
          widget_id: data.widgetId,
          widget_data: {
            selected_options: data.selectedOptions.length > 0 ? data.selectedOptions : undefined,
            button_action: data.buttonAction,
            feedback_text: data.feedbackText,
          },
        },
        {
          onStart: (e) => {
            const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
            if (cur) {
              const updated = { ...cur, threadId: e.thread_id, runId: e.run_id, isActive: e.is_active ?? cur.isActive };
              chatApiService.syncSession(updated);
              queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
            }
          },
          onState: (e) => {
            if (e.state === 'routing' || e.state === 'reasoning' || e.state === 'tool_use') hadRouting = true;
            if (e.detail) {
              const existing = capturedReasoning.find((r) => r.type === e.state);
              if (existing) existing.text = existing.text + '\n' + e.detail;
              else capturedReasoning.push({ type: e.state, text: e.detail });
            }
          },
          onReasoning: (e) => {
            const reasoningType = e.reasoning_type ?? 'reasoning';
            const existing = capturedReasoning.find((r) => r.type === reasoningType);
            if (existing) existing.text += e.content;
            else capturedReasoning.push({ type: reasoningType, text: e.content });
          },
          onContent: (e) => { accumulated += e.delta; },
          onWidget: (e) => { capturedWidget = e; },
          onNavigate: (e) => {
            navigatePanel = e.panel;
            navigateWorkflowId = e.params?.workflow_id;
            if (e.params?.workflow_id) {
              const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
              if (cur) {
                const updated: ChatSession = {
                  ...cur,
                  insightsContext: {
                    ...cur.insightsContext,
                    workflowId: e.params.workflow_id,
                    checkpointId: e.params.checkpoint_id ?? cur.insightsContext?.checkpointId ?? '',
                  },
                };
                chatApiService.syncSession(updated);
                queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
              }
            }
          },
          onDisplay: (e) => { if (!contentSummaryReceived) capturedDisplayContent = mapDisplayEvent(e); },
          onContentSummary: (e) => { contentSummaryReceived = true; capturedDisplayContent = mapDisplayEvent(e); },
          onThreadSummary: (e) => {
            capturedDisplayContent = mapDisplayEvent(e);
            const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
            if (cur) {
              const prev = cur.handlerResults?.['thread-summarisation'];
              const updated: ChatSession = {
                ...cur,
                handlerType: 'thread-summarisation',
                handlerResults: {
                  ...cur.handlerResults,
                  'thread-summarisation': {
                    thread_id: prev?.thread_id ?? cur.summaryThreadId ?? '',
                    overall_summary: e.content.text ?? '',
                    attached_documents: prev?.attached_documents ?? null,
                    chat_message: prev?.chat_message ?? '',
                    status: prev?.status ?? 'completed',
                  },
                },
              };
              chatApiService.syncSession(updated);
              queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
            }
          },
          onSuggestedActions: (e) => { capturedSuggestedActions.push(...e.actions); },
          onReport: (e) => {
            capturedReportData = e.data;
            capturedReportWorkflowId = e.workflow_id;
            const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
            if (cur) {
              const updated: ChatSession = {
                ...cur,
                insightsContext: {
                  ...(cur.insightsContext ?? { workflowId: e.workflow_id, checkpointId: '' }),
                  workflowId: e.workflow_id,
                  reportData: e.data,
                },
              };
              chatApiService.syncSession(updated);
              queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
            }
          },
          onInsightsContext: (e) => {
            const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
            if (cur) {
              const updated: ChatSession = {
                ...cur,
                insightsContext: { ...cur.insightsContext, workflowId: e.workflow_id, checkpointId: e.checkpoint_id },
              };
              chatApiService.syncSession(updated);
              queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
            }
          },
          onDone: (e) => {
            const summaryOnly = contentSummaryReceived && !e.content;
            const effectiveContent = summaryOnly ? '' : (accumulated || e.content || capturedDisplayContent?.content_data || '');
            commitFinalSession(queryClient, effectiveContent, navigatePanel, '', hadRouting, {
              reasoning: capturedReasoning,
              suggestedActions: capturedSuggestedActions,
              reportData: capturedReportData,
              reportWorkflowId: capturedReportWorkflowId,
              widgetEvent: capturedWidget,
              navigateWorkflowId,
              displayContent: capturedDisplayContent,
              messageId: e.message_id,
            });
          },
          onNotice: (e) => { showWarning(e.message); },
          onError: (e) => { showError(e.message || 'Widget submission failed'); },
          onStreamError: (err) => {
            if (isAbortError(err)) return;
            const errorText = err.message || 'Something went wrong. Please try again.';
            showError(errorText);
            const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
            if (cur) {
              const errorMsg: AssistantMessage = {
                id: generateMessageId(),
                content: errorText,
                sender: 'assistant',
                timestamp: new Date(),
              };
              const updated: ChatSession = { ...cur, messages: [...cur.messages, errorMsg], updatedAt: new Date() };
              chatApiService.syncSession(updated);
              queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
            }
          },
        },
        controller.signal,
      );
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to submit widget response');
    },
  });
};

/**
 * Mutation for POST /api/v1/chat/threads/{thread_id}/feedback — rating +
 * optional comment on a specific assistant response.
 */
export const useSubmitFeedback = () => {
  const { showError } = useSnackbar();

  return useMutation({
    mutationFn: async (data: {
      threadId: string;
      rating: 'positive' | 'negative';
      comment?: string;
      messageId?: string;
      agentId?: string;
    }) => {
      return chatApiService.submitFeedback(data.threadId, data.rating, {
        comment: data.comment,
        messageId: data.messageId,
        agentId: data.agentId,
      });
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to submit feedback');
    },
  });
};

function mapDisplayEvent(e: NavDisplayEvent | NavContentSummaryEvent | NavThreadSummaryEvent): DisplayContent {
  return {
    content_type: 'markdown',
    content_data: e.content.text ?? '',
    title: e.content.title,
    actions: e.content.actions,
  };
}

const _navigatePanelToTargetId: Record<string, string> = {
  dashboard: 'morning-dashboard-panel',
  diary_management: 'diary-management-panel',
  thread_summarisation: 'thread-summarisation-panel',
  meeting_availability: 'meeting-availability-panel',
  to_do: 'tasks-panel',
  handover_support: 'handover-support-panel',
  insights_hub: 'insights-hub-panel',
  my_account: 'my-account-panel',
  action_items: 'tasks-panel',
};

const _panelParamToUiPage: Record<string, string> = {
  'thread-summarisation-panel': 'email_summarisation',
  'meeting-availability-panel': 'scheduling_and_meetings',
  'tasks-panel': 'to_do',
  'handover-support-panel': 'handover_support',
  'insights-hub-panel': 'insights_hub',
};

const REASONING_LABELS: Record<string, string> = {
  reasoning: 'Reasoning',
  planning: 'Planning',
  tool_selection: 'Tool Selection',
  critique: 'Critique',
  summary: 'Summary',
};

function commitFinalSession(
  queryClient: QueryClient,
  accumulated: string,
  navigatePanel: string | undefined,
  content: string,
  hadRouting: boolean,
  turnData: {
    reasoning: { type: string; text: string }[];
    suggestedActions: NavSuggestedAction[];
    reportData: NavReportData | undefined;
    reportWorkflowId: string | undefined;
    widgetEvent: NavWidgetEvent | undefined;
    navigateWorkflowId?: string;
    displayContent?: DisplayContent;
    messageId?: string;
  },
): void {
  const { reasoning: capturedReasoning, suggestedActions, reportData, reportWorkflowId, widgetEvent, navigateWorkflowId, displayContent, messageId } = turnData;
  const reasoningSteps = capturedReasoning.length > 0
    ? capturedReasoning.map((r) => ({
        id: r.type,
        label: REASONING_LABELS[r.type] ?? r.type,
        status: 'completed' as const,
        details: [r.text],
      }))
    : hadRouting
      ? [{ id: 'nav-routing', label: 'Analyzed request', status: 'completed' as const }]
      : undefined;
  // Navigate to Insights Hub on a fresh report OR on a Q&A turn pointing back at an existing report via workflow_id.
  const isInsightsHubNavigate = navigatePanel === 'insights_hub';
  const targetPanelId = navigatePanel
    ? isInsightsHubNavigate
      ? (reportData || navigateWorkflowId ? _navigatePanelToTargetId[navigatePanel] : undefined)
      : _navigatePanelToTargetId[navigatePanel]
    : undefined;

  // For panels that render their content on the right side, promote the
  // streamed content into displayContent and show a short stub in chat.
  let resolvedDisplayContent = displayContent;
  const bubbleContent = accumulated;

  const assistantMsg: AssistantMessage = {
    id: generateMessageId(),
    content: bubbleContent,
    sender: 'assistant',
    timestamp: new Date(),
    targetPanelId,
    reasoningSteps,
    displayContent: resolvedDisplayContent,
    suggestedActions: suggestedActions.length > 0 ? suggestedActions : undefined,
    reportData: isInsightsHubNavigate && reportData ? reportData : undefined,
    reportWorkflowId: isInsightsHubNavigate && reportData ? reportWorkflowId : undefined,
    navigateOverviewId: isInsightsHubNavigate && !reportData ? navigateWorkflowId : undefined,
    widgetId: widgetEvent?.widget_id,
    widgetType: widgetEvent?.config.widget_type,
    widgetData: widgetEvent ? {
      widget_id: widgetEvent.widget_id,
      widget_type: widgetEvent.config.widget_type,
      title: widgetEvent.config.title,
      description: widgetEvent.config.description,
      groups: widgetEvent.config.groups,
      buttons: widgetEvent.config.buttons,
      layout: widgetEvent.config.layout,
      submit_button: widgetEvent.config.submit_button,
      cancel_button: widgetEvent.config.cancel_button,
      allow_feedback: widgetEvent.config.allow_feedback,
      feedback_prompt: widgetEvent.config.feedback_prompt,
    } : undefined,
    messageId,
  };
  const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
  if (!cur) return;

  const isFirstUserMsg = cur.messages.filter((m) => m.sender === 'user').length === 1;
  const defaultTitle = cur.title === 'New Chat' && isFirstUserMsg;
  let title = cur.title;
  if (defaultTitle) {
    title = content.length > 50 ? `${content.substring(0, 47)}...` : content;
  }

  const finalSession: ChatSession = {
    ...cur,
    messages: [...cur.messages, assistantMsg],
    updatedAt: new Date(),
    title,
  };
  chatApiService.syncSession(finalSession);
  queryClient.setQueryData(chatQueryKeys.currentSession(), finalSession);
  const prevSessions = queryClient.getQueryData<ChatSession[]>(chatQueryKeys.sessions()) ?? [];
  queryClient.setQueryData(
    chatQueryKeys.sessions(),
    [finalSession, ...prevSessions.filter((s) => s.id !== finalSession.id)],
  );
}

/**
 * SSE streaming hook — replaces useSendMessage for the /api/v1/chat/chat-stream endpoint.
 * Exposes isPending, isRouting, and streamingContent for live UI feedback.
 */
export const useStreamChat = () => {
  const queryClient = useQueryClient();
  const { showError, showWarning } = useSnackbar();

  const [isPending, setIsPending] = useState(false);
  const [isRouting, setIsRouting] = useState(false);
  const [wasRouting, setWasRouting] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [liveReasoning, setLiveReasoning] = useState<{ type: string; text: string }[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  // Tracks which session ID is running in sample-mode (triggered by "please mock")
  const sampleSessionRef = useRef<string | null>(null);
  // Identifies the in-flight run for server-side cancel; cleared once it finishes.
  const activeRunRef = useRef<{ threadId: string; runId: string } | null>(null);
  // The checkpoint to roll back to if this turn gets cancelled.
  const currentTurnCheckpointRef = useRef<{ id: string; restorable: boolean } | null>(null);

  const abort = useCallback(() => {
    abortRef.current?.abort();

    const activeRun = activeRunRef.current;
    const checkpoint = currentTurnCheckpointRef.current;
    activeRunRef.current = null;
    if (!activeRun) return;

    // Best-effort server-side cancel + restore-to-checkpoint via Robin Chat -
    // Experimental (/api/v1/robin-chat/*) — no equivalent exists under
    // /api/v1/chat/*. thread_id came from a /chat/chat-stream session, so
    // the robin-chat backend may not recognize it (different agent/thread
    // namespace); failures here are silent since the client-side abort
    // already stopped the local stream regardless.
    void cancelStream(activeRun.threadId, activeRun.runId)
      .then(() => {
        if (checkpoint?.restorable) {
          return restoreCheckpoint(activeRun.threadId, checkpoint.id).then(() => undefined);
        }
        return undefined;
      })
      .catch(() => {});
  }, []);

  // Delegates to the module-level commitFinalSession, keeping queryClient in scope.
  const _commit = useCallback(
    (...args: Parameters<typeof commitFinalSession> extends [QueryClient, ...infer R] ? R : never) =>
      commitFinalSession(queryClient, ...args),
    [queryClient],
  );

  const mutateAsync = useCallback(
    async ({ content, context, signal }: { content: string; context?: MessageContext; signal?: AbortSignal }) => {
      const controller = new AbortController();
      abortRef.current = controller;
      if (signal) signal.addEventListener('abort', () => controller.abort());

      // Start a blank session when the action requests a fresh thread
      const session = context?.startNewThread
        ? await chatApiService.createSession()
        : await chatApiService.getOrCreateSession();

      // Priority: session's own insightsContext (set via SSE or right-panel sync) >
      // pending context stored by the right panel before this session became current >
      // URL param fallback for workflow_id only.
      const pendingCtx = chatApiService.getPendingInsightsContext();
      const urlParams = new URLSearchParams(window.location.search);
      const isInsightsHubPanel = urlParams.get('panel') === 'insights-hub-panel';
      const overviewIdFromUrl = urlParams.get('overview-id') ?? undefined;
      const currentUiPage = _panelParamToUiPage[urlParams.get('panel') ?? ''] ?? 'dashboard';
      const workflowId = session.insightsContext?.workflowId
        ?? pendingCtx?.workflowId
        ?? (isInsightsHubPanel ? overviewIdFromUrl : undefined);
      let checkpointId = session.insightsContext?.checkpointId || pendingCtx?.checkpointId || undefined;

      // If we have a workflowId but no checkpoint, fetch it from the Insights Hub API
      // so the backend can resume from the correct state.
      if (workflowId && !checkpointId) {
        checkpointId = await chatApiService.resolveCheckpointId(workflowId);
      }

      // Determine endpoint: "please mock" triggers sample mode for this session;
      // all subsequent messages in the same session reuse the sample endpoint.
      // Insights Hub uses the same production navigator endpoint as everywhere
      // else (not /api/v1/robin-chat/*) — this was tried and reverted, see
      // .vscode/insighthubs-456773-robin-chat-2.0-data-management.md.
      const isMockTrigger = content.trim().toLowerCase() === 'please mock';
      if (isMockTrigger) sampleSessionRef.current = session.id;
      const isSampleSession = sampleSessionRef.current === session.id;
      const endpoint = isSampleSession
        ? '/api/v1/chat/chat-stream-sample'
        : '/api/v1/chat/chat-stream';

      const userMessage: ChatMessage = {
        id: generateMessageId(),
        content: content.trim(),
        sender: 'user',
        timestamp: new Date(),
        context,
      };
      const sessionWithUser: ChatSession = {
        ...session,
        messages: [...session.messages, userMessage],
        updatedAt: new Date(),
      };
      chatApiService.syncSession(sessionWithUser);
      queryClient.setQueryData(chatQueryKeys.currentSession(), sessionWithUser);
      const prevSessions = queryClient.getQueryData<ChatSession[]>(chatQueryKeys.sessions()) ?? [];
      queryClient.setQueryData(
        chatQueryKeys.sessions(),
        [sessionWithUser, ...prevSessions.filter((s) => s.id !== sessionWithUser.id)],
      );

      setIsPending(true);
      setIsRouting(false);
      setWasRouting(false);
      setStreamingContent('');
      setLiveReasoning([]);

      let accumulated = '';
      let navigatePanel: string | undefined;
      let navigateWorkflowId: string | undefined;
      let hadRouting = false;
      const capturedReasoning: { type: string; text: string }[] = [];
      const capturedSuggestedActions: NavSuggestedAction[] = [];
      // Scoped to this turn only — never fall back to a previous turn's report.
      let capturedReportData: NavReportData | undefined;
      let capturedReportWorkflowId: string | undefined;
      let capturedWidget: NavWidgetEvent | undefined;
      let capturedDisplayContent: DisplayContent | undefined;
      let contentSummaryReceived = false;
      let contentSummaryText = '';

      try {
        await streamChat(
          {
            // thread_id must be omitted to start a new conversation — the backend
            // 404s ("Conversation not found or access denied") on any client-made-up
            // ID that it didn't issue itself. The real thread_id comes back on the
            // `start` event and is reused (via session.threadId) from then on.
            thread_id: context?.startNewThread ? undefined : (session.threadId ?? undefined),
            message: content,
            workflow_id: workflowId,
            checkpoint_id: checkpointId,
            summary_thread_id: context?.startNewThread ? undefined : (session.summaryThreadId ?? undefined),
            current_ui_page: context?.targetUiPage || currentUiPage,
            start_new_thread: context?.startNewThread || undefined,
            target_agent: context?.targetAgent || undefined,
            target_ui_page: context?.targetUiPage || undefined,
          },
          {
            onStart: (e) => {
              activeRunRef.current = { threadId: e.thread_id, runId: e.run_id };
              currentTurnCheckpointRef.current = e.checkpoint_id
                ? { id: e.checkpoint_id, restorable: e.checkpoint_restorable }
                : null;

              const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
              if (cur) {
                const updated: ChatSession = {
                  ...cur,
                  threadId: e.thread_id,
                  runId: e.run_id,
                  isActive: e.is_active ?? cur.isActive,
                  // New thread from backend: clear stale summary context
                  ...(e.is_new ? { summaryThreadId: null } : {}),
                };
                chatApiService.syncSession(updated);
                queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
              }
            },
            onState: (e) => {
              if (e.state === 'routing' || e.state === 'reasoning' || e.state === 'tool_use') {
                hadRouting = true;
                setWasRouting(true);
                setIsRouting(true);
              } else {
                setIsRouting(false);
              }
              // Capture detail text so it appears in the ThinkingIndicator
              if (e.detail) {
                const existing = capturedReasoning.find((r) => r.type === e.state);
                if (existing) {
                  existing.text = existing.text + '\n' + e.detail;
                } else {
                  capturedReasoning.push({ type: e.state, text: e.detail });
                }
                setLiveReasoning([...capturedReasoning]);
              }
            },
            onReasoning: (e) => {
              // Robin Chat - Experimental's reasoning event has no reasoning_type
              // field (unlike the navigator's), so e.reasoning_type is undefined
              // at runtime here — fall back to a fixed bucket instead of letting
              // `undefined` become a React key / blank label downstream.
              const reasoningType = e.reasoning_type ?? 'reasoning';
              const existing = capturedReasoning.find((r) => r.type === reasoningType);
              if (existing) {
                existing.text += e.content;
              } else {
                capturedReasoning.push({ type: reasoningType, text: e.content });
              }
              setLiveReasoning([...capturedReasoning]);
            },
            onContent: (e) => { accumulated += e.delta; if (!contentSummaryReceived) setStreamingContent(accumulated); },
            onWidget: (e) => { capturedWidget = e; },
            onNavigate: (e) => {
              navigatePanel = e.panel;
              navigateWorkflowId = e.params?.workflow_id;
              if (e.params?.workflow_id) {
                const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
                if (cur) {
                  const updated: ChatSession = {
                    ...cur,
                    insightsContext: {
                      ...cur.insightsContext,
                      workflowId: e.params.workflow_id,
                      checkpointId: e.params.checkpoint_id ?? cur.insightsContext?.checkpointId ?? '',
                    },
                  };
                  chatApiService.syncSession(updated);
                  queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
                }
              }
            },
            onDisplay: (e) => { if (!contentSummaryReceived) capturedDisplayContent = mapDisplayEvent(e); },
            onContentSummary: (e) => { contentSummaryReceived = true; capturedDisplayContent = mapDisplayEvent(e); contentSummaryText = e.content.text ?? ''; setStreamingContent(''); },
            onThreadSummary: (e) => {
              capturedDisplayContent = mapDisplayEvent(e);
              const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
              if (cur) {
                const prev = cur.handlerResults?.['thread-summarisation'];
                const updated: ChatSession = {
                  ...cur,
                  handlerType: 'thread-summarisation',
                  handlerResults: {
                    ...cur.handlerResults,
                    'thread-summarisation': {
                      thread_id: prev?.thread_id ?? cur.summaryThreadId ?? '',
                      overall_summary: e.content.text ?? '',
                      attached_documents: prev?.attached_documents ?? null,
                      chat_message: prev?.chat_message ?? '',
                      status: prev?.status ?? 'completed',
                    },
                  },
                };
                chatApiService.syncSession(updated);
                queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
              }
            },
            onSuggestedActions: (e) => { capturedSuggestedActions.push(...e.actions); },
            onReport: (e) => {
              capturedReportData = e.data;
              capturedReportWorkflowId = e.workflow_id;

              // Also persist onto insightsContext for follow-up-message context
              // continuity — separate concern from the panel-render trigger
              // above, which uses the turn-scoped capture instead so a later
              // turn without its own report event never reuses a stale one.
              const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
              if (cur) {
                const updated: ChatSession = {
                  ...cur,
                  insightsContext: {
                    ...(cur.insightsContext ?? { workflowId: e.workflow_id, checkpointId: '' }),
                    workflowId: e.workflow_id,
                    reportData: e.data,
                  },
                };
                chatApiService.syncSession(updated);
                queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
              }
            },
            onInsightsContext: (e) => {
              // Persist workflow_id + checkpoint_id so future messages in this
              // session automatically target the same Insights Hub context.
              // Merge onto any existing insightsContext (spread first) instead of
              // replacing it — a `report` event earlier in the same stream may
              // have already stored reportData, which this event's payload
              // doesn't carry and would otherwise silently wipe out, breaking
              // the auto-navigate-to-Insights-Hub-with-the-new-report flow.
              const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
              if (cur) {
                const updated: ChatSession = {
                  ...cur,
                  insightsContext: {
                    ...cur.insightsContext,
                    workflowId: e.workflow_id,
                    checkpointId: e.checkpoint_id,
                  },
                };
                chatApiService.syncSession(updated);
                queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
              }
            },
            onError: (e) => { showError(e.message || 'An error occurred'); },
            onNotice: (e) => { showWarning(e.message); },
            onDone: (e) => {
              activeRunRef.current = null;
              // Suppress chat content when it duplicates the right-panel summary
              const normalise = (s: string) => s.replace(/\s+/g, ' ').trim();
              const isDuplicate = contentSummaryReceived && normalise(accumulated) === normalise(contentSummaryText);
              const effectiveContent = isDuplicate ? '' : (accumulated || e.content || capturedDisplayContent?.content_data || '');
              _commit(
                effectiveContent,
                navigatePanel,
                content,
                hadRouting,
                {
                  reasoning: capturedReasoning,
                  suggestedActions: capturedSuggestedActions,
                  reportData: capturedReportData,
                  reportWorkflowId: capturedReportWorkflowId,
                  widgetEvent: capturedWidget,
                  navigateWorkflowId,
                  displayContent: capturedDisplayContent,
                  messageId: e.message_id,
                },
              );
            },
            onStreamError: (err) => {
              activeRunRef.current = null;
              if (isAbortError(err)) return;

              const errorText = err.message || 'Something went wrong. Please try again.';
              showError(errorText);

              // Add an error assistant message so the failure is visible inline.
              const cur = queryClient.getQueryData<ChatSession | null>(chatQueryKeys.currentSession());
              if (cur) {
                const errorMsg: AssistantMessage = {
                  id: generateMessageId(),
                  content: errorText,
                  sender: 'assistant',
                  timestamp: new Date(),
                };
                const updated: ChatSession = { ...cur, messages: [...cur.messages, errorMsg], updatedAt: new Date() };
                chatApiService.syncSession(updated);
                queryClient.setQueryData(chatQueryKeys.currentSession(), updated);
              }

              queryClient.invalidateQueries({ queryKey: chatQueryKeys.sessions() });
            },
          },
          controller.signal,
          endpoint,
        );
      } finally {
        setStreamingContent('');
        setLiveReasoning([]);
        setIsRouting(false);
        setWasRouting(false);
        setIsPending(false);
      }
    },
    [queryClient, showError, showWarning, _commit],
  );

  return { mutateAsync, isPending, isRouting, wasRouting, streamingContent, liveReasoning, abort };
};
